// Google Gemini API Client
// https://ai.google.dev/gemini-api/docs

import { createRequestAPI } from './utils.js'

/**
 * Create Gemini client
 * @param {Function} httpRequest - HTTP request function
 * @returns {Object} Gemini client with generateContent, listModels, getModel, chat
 */
export function createGeminiClient(httpRequest) {
  const requestAPI = createRequestAPI(httpRequest)

  // https://ai.google.dev/gemini-api/docs/text-generation
  const generateContent = async ({
    apiKey,
    model = 'gemini-2.5-flash',
    contents,
    generationConfig,
    safetySettings,
    tools,
    toolConfig,
    systemInstruction,
  }) => {
    if (!contents || !Array.isArray(contents) || contents.length === 0) {
      throw new Error('contents is required and must be a non-empty array')
    }

    const bodyParams = {
      contents,
      generationConfig,
      safetySettings,
      tools,
      toolConfig,
      systemInstruction,
    }

    const body = Object.fromEntries(
      Object.entries(bodyParams).filter(([_, value]) => value !== undefined),
    )

    return await requestAPI({
      path: `/v1beta/models/${model}:generateContent`,
      method: 'POST',
      apiKey,
      body,
    })
  }

  // https://ai.google.dev/gemini-api/docs/models/gemini
  const listModels = async ({ apiKey, pageSize, pageToken }) => {
    let path = '/v1beta/models'
    const params = []

    if (pageSize) {
      params.push(`pageSize=${pageSize}`)
    }
    if (pageToken) {
      params.push(`pageToken=${pageToken}`)
    }

    if (params.length > 0) {
      path += '?' + params.join('&')
    }

    return await requestAPI({
      path,
      method: 'GET',
      apiKey,
      body: {},
    })
  }

  // https://ai.google.dev/gemini-api/docs/models/gemini
  const getModel = async ({ apiKey, model }) => {
    if (!model) {
      throw new Error('model is required')
    }

    return await requestAPI({
      path: `/v1beta/models/${model}`,
      method: 'GET',
      apiKey,
      body: {},
    })
  }

  // https://ai.google.dev/gemini-api/docs/text-generation#chat
  const chat = async ({
    apiKey,
    model = 'gemini-2.5-flash',
    messages,
    temperature,
    maxOutputTokens,
    topP,
    topK,
  }) => {
    if (!messages || !Array.isArray(messages) || messages.length === 0) {
      throw new Error('messages is required and must be a non-empty array')
    }

    // Convert messages to Gemini format
    const contents = messages.map((msg) => ({
      role: msg.role === 'system' ? 'user' : msg.role,
      parts: [{ text: msg.content }],
    }))

    // Build generation config
    const generationConfig = {}
    if (temperature !== undefined) generationConfig.temperature = temperature
    if (maxOutputTokens !== undefined)
      generationConfig.maxOutputTokens = maxOutputTokens
    if (topP !== undefined) generationConfig.topP = topP
    if (topK !== undefined) generationConfig.topK = topK

    return await generateContent({
      apiKey,
      model,
      contents,
      generationConfig:
        Object.keys(generationConfig).length > 0 ? generationConfig : undefined,
    })
  }

  return {
    generateContent,
    listModels,
    getModel,
    chat,
  }
}
